import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, new_password: password }
      );
      setSuccess(true);
      toast.success('Password reset! You can now sign in.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#2D2A26]">Invalid Reset Link</h1>
          <Link to="/forgot-password" className="text-[#6B7A4F] mt-4 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6 paper-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <BookOpen className="w-8 h-8 text-[#6B7A4F]" />
            <span className="text-2xl font-bold text-[#2D2A26]">Bookish Bestie</span>
          </div>
          <h1 className="text-3xl font-light text-[#2D2A26]">
            Set New <span className="font-bold">Password</span>
          </h1>
        </div>
        <div className="glass-card p-8 rounded-2xl space-y-6">
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-[#8E9E82] mx-auto" />
              <h3 className="text-xl font-semibold text-[#2D2A26]">Password Reset!</h3>
              <p className="text-[#5C5955]">Redirecting to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="pwd">New Password</Label>
                <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" className="bg-white/50 border-[#E1D9CD] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="conf">Confirm Password</Label>
                <Input id="conf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-password-confirm" className="bg-white/50 border-[#E1D9CD] rounded-xl" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-password-submit" className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white py-6 rounded-full font-semibold">
                {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Resetting...</> : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
